import React from 'react';
import { Select, Typography } from 'antd';

const { Text } = Typography;

/**
 * BoxModelControl - Unified box model visualization
 * Shows margin, border, padding, and content in one nested display
 */
const BoxModelControl = ({
    margin = {},
    padding = {},
    gap = {},
    onMarginChange,
    onPaddingChange,
    onGapChange,
    tokens = []
}) => {
    // Convert spacing tokens to select options
    const tokenOptions = [
        { value: '0', label: '0', px: 0 },
        ...tokens.map(t => ({
            value: t.name,
            label: `${t.name}`,
            px: t.values?.mobile || 0
        }))
    ];

    // Get pixel value for display
    const getPx = (tokenName) => {
        if (!tokenName || tokenName === '0') return 0;
        const token = tokens.find(t => t.name === tokenName);
        return token?.values?.mobile || parseInt(tokenName, 10) || 0;
    };

    const handleMarginChange = (direction, value) => {
        onMarginChange?.({ ...margin, [direction]: value });
    };

    const handlePaddingChange = (direction, value) => {
        onPaddingChange?.({ ...padding, [direction]: value });
    };

    return (
        <div style={{ marginBottom: 16 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 12 }}>
                BOX MODEL
            </Text>

            {/* Unified Box Model Display */}
            <div style={{
                background: '#161b22',
                borderRadius: 8,
                padding: 12,
                fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
                {/* Margin Layer */}
                <div style={{
                    background: 'rgba(255, 183, 128, 0.12)',
                    border: '1px solid rgba(255, 183, 128, 0.3)',
                    borderRadius: 6,
                    padding: 6,
                    position: 'relative'
                }}>
                    <span style={{
                        position: 'absolute',
                        top: 4,
                        left: 8,
                        fontSize: 9,
                        color: '#8b949e',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        margin
                    </span>

                    {/* Top margin */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4, marginTop: 14 }}>
                        <Select
                            size="small"
                            value={margin.top || '0'}
                            onChange={(v) => handleMarginChange('top', v)}
                            options={tokenOptions}
                            style={{ width: 70 }}
                            bordered={false}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {/* Left margin */}
                        <Select
                            size="small"
                            value={margin.left || '0'}
                            onChange={(v) => handleMarginChange('left', v)}
                            options={tokenOptions}
                            style={{ width: 60 }}
                            bordered={false}
                        />

                        {/* Padding Layer */}
                        <div style={{
                            flex: 1,
                            background: 'rgba(130, 200, 130, 0.12)',
                            border: '1px solid rgba(130, 200, 130, 0.3)',
                            borderRadius: 4,
                            padding: 6
                        }}>
                            <span style={{
                                fontSize: 9,
                                color: '#8b949e',
                                display: 'block',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: 4
                            }}>
                                padding
                            </span>

                            {/* Top padding */}
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                                <Select
                                    size="small"
                                    value={padding.top || '0'}
                                    onChange={(v) => handlePaddingChange('top', v)}
                                    options={tokenOptions}
                                    style={{ width: 70 }}
                                    bordered={false}
                                />
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                {/* Left padding */}
                                <Select
                                    size="small"
                                    value={padding.left || '0'}
                                    onChange={(v) => handlePaddingChange('left', v)}
                                    options={tokenOptions}
                                    style={{ width: 60 }}
                                    bordered={false}
                                />

                                {/* Content */}
                                <div style={{
                                    flex: 1,
                                    background: 'rgba(100, 150, 200, 0.15)',
                                    border: '1px solid rgba(100, 150, 200, 0.3)',
                                    borderRadius: 4,
                                    padding: '12px 8px',
                                    textAlign: 'center',
                                    fontSize: 11,
                                    color: '#8b949e'
                                }}>
                                    content
                                </div>

                                {/* Right padding */}
                                <Select
                                    size="small"
                                    value={padding.right || '0'}
                                    onChange={(v) => handlePaddingChange('right', v)}
                                    options={tokenOptions}
                                    style={{ width: 60 }}
                                    bordered={false}
                                />
                            </div>

                            {/* Bottom padding */}
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
                                <Select
                                    size="small"
                                    value={padding.bottom || '0'}
                                    onChange={(v) => handlePaddingChange('bottom', v)}
                                    options={tokenOptions}
                                    style={{ width: 70 }}
                                    bordered={false}
                                />
                            </div>
                        </div>

                        {/* Right margin */}
                        <Select
                            size="small"
                            value={margin.right || '0'}
                            onChange={(v) => handleMarginChange('right', v)}
                            options={tokenOptions}
                            style={{ width: 60 }}
                            bordered={false}
                        />
                    </div>

                    {/* Bottom margin */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
                        <Select
                            size="small"
                            value={margin.bottom || '0'}
                            onChange={(v) => handleMarginChange('bottom', v)}
                            options={tokenOptions}
                            style={{ width: 70 }}
                            bordered={false}
                        />
                    </div>
                </div>
            </div>

            {/* Gap control */}
            <div style={{ marginTop: 16 }}>
                <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 8 }}>
                    GAP (BETWEEN CHILDREN)
                </Text>
                <Select
                    size="small"
                    value={gap.all || '0'}
                    onChange={(v) => onGapChange?.({ all: v })}
                    options={tokenOptions}
                    style={{ width: '100%' }}
                />
            </div>
        </div>
    );
};

export default BoxModelControl;
